import { useState, useEffect } from 'react';
import Navigation from '../../home/components/Navigation';
import Footer from '../../home/components/Footer';

export default function QuarterlyTaxEstimator() {
  useEffect(() => {
    document.title = "Quarterly Tax Estimator 2025 | Estimated Tax Payment Calculator - ItsJustTax";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Calculate quarterly estimated tax payments for 2025. Estimate Form 1040-ES payments for self-employed, freelancers, and contractors. Avoid IRS penalties with accurate quarterly tax estimates and payment due dates.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'quarterly taxes, estimated tax payments, Form 1040-ES, quarterly tax calculator, self-employed quarterly taxes');
    }
  }, []);

  const [income, setIncome] = useState('');
  const [expenses, setExpenses] = useState('');
  const [filingStatus, setFilingStatus] = useState('single');
  const [dependents, setDependents] = useState('0');
  const [otherIncome, setOtherIncome] = useState('');
  const [result, setResult] = useState<any>(null);
  const [scrolled, setScrolled] = useState(false);

  const calculate = () => {
    const grossIncome = parseFloat(income);
    const businessExpenses = parseFloat(expenses) || 0;
    const additionalIncome = parseFloat(otherIncome) || 0;
    const numDependents = parseInt(dependents);

    if (isNaN(grossIncome) || grossIncome <= 0) {
      alert('Please enter a valid income amount');
      return;
    }

    const netBusinessIncome = grossIncome - businessExpenses;
    const totalIncome = netBusinessIncome + additionalIncome;

    // Self-employment tax
    const selfEmploymentIncome = netBusinessIncome * 0.9235;
    const seTax = selfEmploymentIncome * 0.153;
    const deductibleSETax = seTax * 0.5;

    // Income tax
    const standardDeduction = filingStatus === 'single' ? 14600 : filingStatus === 'married' ? 29200 : 21900;
    const taxableIncome = Math.max(0, totalIncome - deductibleSETax - standardDeduction);
    
    let incomeTax = 0;
    if (filingStatus === 'single') {
      if (taxableIncome <= 11600) incomeTax = taxableIncome * 0.10;
      else if (taxableIncome <= 47150) incomeTax = 1160 + (taxableIncome - 11600) * 0.12;
      else if (taxableIncome <= 100525) incomeTax = 5426 + (taxableIncome - 47150) * 0.22;
      else if (taxableIncome <= 191950) incomeTax = 17168.50 + (taxableIncome - 100525) * 0.24;
      else if (taxableIncome <= 243725) incomeTax = 39110.50 + (taxableIncome - 191950) * 0.32;
      else if (taxableIncome <= 609350) incomeTax = 55678.50 + (taxableIncome - 243725) * 0.35;
      else incomeTax = 183647.50 + (taxableIncome - 609350) * 0.37;
    } else if (filingStatus === 'married') {
      if (taxableIncome <= 23200) incomeTax = taxableIncome * 0.10;
      else if (taxableIncome <= 94300) incomeTax = 2320 + (taxableIncome - 23200) * 0.12;
      else if (taxableIncome <= 201050) incomeTax = 10852 + (taxableIncome - 94300) * 0.22;
      else if (taxableIncome <= 383900) incomeTax = 34337 + (taxableIncome - 201050) * 0.24;
      else if (taxableIncome <= 487450) incomeTax = 78221 + (taxableIncome - 383900) * 0.32;
      else if (taxableIncome <= 731200) incomeTax = 111357 + (taxableIncome - 487450) * 0.35;
      else incomeTax = 196669.50 + (taxableIncome - 731200) * 0.37;
    } else {
      if (taxableIncome <= 16550) incomeTax = taxableIncome * 0.10;
      else if (taxableIncome <= 63100) incomeTax = 1655 + (taxableIncome - 16550) * 0.12;
      else if (taxableIncome <= 100500) incomeTax = 7241 + (taxableIncome - 63100) * 0.22;
      else if (taxableIncome <= 191950) incomeTax = 15469 + (taxableIncome - 100500) * 0.24;
      else if (taxableIncome <= 243700) incomeTax = 37417 + (taxableIncome - 191950) * 0.32;
      else if (taxableIncome <= 609350) incomeTax = 53977 + (taxableIncome - 243700) * 0.35;
      else incomeTax = 181954.50 + (taxableIncome - 609350) * 0.37;
    }

    const childCredit = numDependents * 2000;
    const totalTax = Math.max(0, seTax + incomeTax - childCredit);
    const quarterlyPayment = totalTax / 4;

    setResult({
      totalIncome,
      seTax,
      incomeTax,
      childCredit,
      totalTax,
      quarterlyPayment,
      q1: quarterlyPayment,
      q2: quarterlyPayment,
      q3: quarterlyPayment,
      q4: quarterlyPayment
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation scrolled={scrolled} />
      
      <div className="pt-32 pb-20">
        <div className="max-w-5xl mx-auto px-6">
          {/* ... rest of the content remains the same ... */}
          <div className="mb-8">
            <a href="/" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 mb-4 cursor-pointer">
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </a>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Quarterly Tax Estimator</h1>
            <p className="text-lg text-gray-600">Calculate your estimated quarterly tax payments for 2026</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Income Information</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Expected Annual Income</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={income}
                      onChange={(e) => setIncome(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="100000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Business Expenses</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={expenses}
                      onChange={(e) => setExpenses(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="20000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Other Income</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg">$</span>
                    <input
                      type="number"
                      value={otherIncome}
                      onChange={(e) => setOtherIncome(e.target.value)}
                      className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                      placeholder="0"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">Interest, dividends, rental income, etc.</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Filing Status</label>
                  <select
                    value={filingStatus}
                    onChange={(e) => setFilingStatus(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg cursor-pointer"
                  >
                    <option value="single">Single</option>
                    <option value="married">Married Filing Jointly</option>
                    <option value="head">Head of Household</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Number of Dependents</label>
                  <input
                    type="number"
                    value={dependents}
                    onChange={(e) => setDependents(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none text-lg"
                    placeholder="0"
                    min="0"
                  />
                </div>

                <button
                  onClick={calculate}
                  className="w-full py-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl font-semibold hover:from-teal-700 hover:to-teal-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Calculate Payments
                </button>
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-8 shadow-lg text-white">
              <h2 className="text-2xl font-bold mb-6">Payment Schedule</h2>
              
              {result ? (
                <div className="space-y-6">
                  <div className="bg-white/10 rounded-xl p-6">
                    <div className="text-sm text-teal-100 mb-1">Quarterly Payment Amount</div>
                    <div className="text-4xl font-bold">${result.quarterlyPayment.toLocaleString(undefined, {maximumFractionDigits: 2})}</div>
                  </div>

                  <div className="space-y-3">
                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">Q1 Payment</span>
                        <span className="text-2xl font-bold">${result.q1.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="text-sm text-teal-100">Due: April 15, 2026</div>
                    </div>

                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">Q2 Payment</span>
                        <span className="text-2xl font-bold">${result.q2.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="text-sm text-teal-100">Due: June 15, 2026</div>
                    </div>

                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">Q3 Payment</span>
                        <span className="text-2xl font-bold">${result.q3.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="text-sm text-teal-100">Due: September 15, 2026</div>
                    </div>

                    <div className="bg-white/10 rounded-xl p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold">Q4 Payment</span>
                        <span className="text-2xl font-bold">${result.q4.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="text-sm text-teal-100">Due: January 15, 2027</div>
                    </div>
                  </div>

                  <div className="bg-white/10 rounded-xl p-6 mt-6">
                    <h3 className="font-semibold mb-3">Tax Breakdown</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-teal-100">Self-Employment Tax</span>
                        <span>${result.seTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-teal-100">Income Tax</span>
                        <span>${result.incomeTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-teal-100">Child Tax Credits</span>
                        <span>-${result.childCredit.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-white/20 font-semibold">
                        <span>Total Annual Tax</span>
                        <span>${result.totalTax.toLocaleString(undefined, {maximumFractionDigits: 2})}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center text-teal-100">
                    <i className="ri-calendar-check-line text-6xl mb-4 opacity-50"></i>
                    <p>Enter your income information to calculate quarterly payments</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-12 bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Important Reminders</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-alarm-warning-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Avoid Penalties</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Pay at least 90% of your current year tax or 100% of last year's tax to avoid underpayment penalties.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-calendar-event-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Payment Methods</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Pay online through IRS Direct Pay, EFTPS, or by mail with Form 1040-ES vouchers.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-refresh-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Adjust as Needed</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    If your income changes significantly, recalculate and adjust your remaining quarterly payments.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-file-list-3-line text-2xl text-teal-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Keep Records</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Save confirmation numbers and receipts for all quarterly tax payments made throughout the year.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}